/**
 * @author Mohit Kukreja <mohit.kukreja@314ecorp.com>
 * @description Write Off Component
 */
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { InputNumber, Flex, Form, Button, Select, Table, Modal, ButtonProps, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Plus } from '@icon-park/react';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import useSearchGuarantors from 'hooks/patients/useSearchGuarantors';
import useWriteOffReasons from 'hooks/admin/useWriteOffReasons';
import useAllClaimStatusRequests from 'hooks/claim/useAllClaimStatusRequests';
import { filterDataKeys } from 'utils/adfUtils';
import { getPaymentsPath } from 'utils/router';
import { prepareManualWorklistModificationCriteria } from 'utils/claims/workLists';
import { SYSTEM_DEFINED_ID_PREFIX } from 'utils/admin/entitySettings';
import { prepareQueryRule, prepareQueryGroup } from 'utils/queryBuilder';
import { formatAmount, getNameFromFhirResource } from 'utils/accounts/allAccounts';
import { ALLOCATION_MODES } from 'app-constants/payments';
import { CLAIM_WORK_STATES, GROUPED_CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';

type IWriteOff = PatientPayment.IClaimInfo['writeOff'];

type IWriteOffData = {
	claims: ClaimStatusRequests.IResponseModel[];
	reason: string;
};

export interface IProps extends ButtonProps {
	claimId?: string;
	writeOffData?: IWriteOff;
	showAddIcon?: boolean;
	acknowledgeUnallocated?: boolean;
	guarantorData?: Guarantor.IGuarantorResponse[];
	filterSchema?: JsonTree;
	tableSelectionMeta?: ITableSelectionMeta;
	onWriteOffChange: (metadata: IWriteOffData) => void;
	isPending: boolean;
}

// claim id rule for query builder
const getClaimIdRule = (claimId: string) => {
	return prepareQueryRule({
		field: filterDataKeys.claimId,
		operator: OPERATORS.equal,
		value: [claimId],
		valueType: ['text'],
	});
};

// check if claim is eligible for write-off
const isRowSelectable = (claimItem: ClaimStatusRequests.IResponseModel): boolean => {
	const workState = claimItem?.claimMetaData?.workState;
	const pendingPR = claimItem?.claimMetaData?.pendingPR;
	return workState === CLAIM_WORK_STATES.a_payment_posted.value && !!pendingPR;
};

// get total pending patient responsibility
const getTotalPR = (data: ClaimStatusRequests.IResponseModel[]) => {
	if (!data) {
		return 0;
	}
	return _.reduce(
		data,
		(acc, claim) => {
			const pendingPR = claim?.claimMetaData?.pendingPR ?? 0;
			return acc + pendingPR;
		},
		0,
	);
};
/*
 * Write Off Component
 */
const WriteOff: React.FC<IProps> = ({
	claimId,
	writeOffData,
	showAddIcon,
	acknowledgeUnallocated,
	tableSelectionMeta,
	onWriteOffChange,
	isPending,
	...rest
}) => {
	const [form] = Form.useForm();
	const [open, setOpen] = useState(false);
	// directly show confirm write off modal if claim id is provided
	const [showConfirmWriteOff, setShowConfirmWriteOff] = useState(!!claimId);
	const { data: writeOffReasons, isLoading } = useWriteOffReasons();

	const writeOffReasonsOptions = useMemo(() => {
		const validUserWriteOffReasons = _.filter(
			writeOffReasons,
			(writeOffReason) => !_.startsWith(writeOffReason?.id, SYSTEM_DEFINED_ID_PREFIX) && !writeOffReason?.isDeleted,
		);
		return _.map(validUserWriteOffReasons, (reason) => ({ label: reason.name, value: reason.id }));
	}, [writeOffReasons]);

	// if claim id is provided, use claim id rule, otherwise use table selection meta criteria
	const criteria = useMemo(() => {
		if (claimId) {
			return prepareQueryGroup({ children: [getClaimIdRule(claimId)] });
		}
		return prepareManualWorklistModificationCriteria(tableSelectionMeta);
	}, [claimId, tableSelectionMeta]);

	// fetch claims matching the criteria
	const {
		data: claimsData,
		isLoading: isLoadingClaims,
		error: isClaimsError,
	} = useAllClaimStatusRequests(
		{
			apiParams: { criteria: criteria ?? null },
			pageSize: tableSelectionMeta?.selectCount ?? 0,
			workStateGroup: GROUPED_CLAIM_WORK_STATES.active.groupValue,
		},
		{ enabled: open && !!criteria },
	);

	// filter claims that are eligible for write-off
	const eligibleClaims = useMemo(() => _.filter(claimsData?.data, isRowSelectable), [claimsData?.data]);
	// get total pending patient responsibility
	const { totalPR, guarantorIds } = useMemo(() => {
		return {
			totalPR: getTotalPR(eligibleClaims),
			guarantorIds: _.uniq(_.compact(_.map(eligibleClaims, (claim) => claim?.account?.guarantorId))),
		};
	}, [eligibleClaims]);

	// Build criteria for guarantor search with guarantor IDs (from data parameter)
	const guarantorSearchCriteria = useMemo(() => {
		if (!_.isEmpty(guarantorIds) && !claimId) {
			const rule = prepareQueryRule({
				field: 'guarantorId',
				operator: OPERATORS.multiselectContains,
				value: [guarantorIds],
				valueType: ['multiselect'],
			});
			return prepareQueryGroup({ children: [rule] });
		}
		return null;
	}, [guarantorIds]);

	// Fetch guarantors using search hook with criteria (only when guarantorIdsToFetch is set)
	const {
		data: guarantorsData,
		isLoading: isLoadingGuarantors,
		isError: isGuarantorsError,
	} = useSearchGuarantors(
		{
			guarantorSearch: {
				criteria: guarantorSearchCriteria,
			},
			pgSize: _.size(guarantorIds),
		},
		{ enabled: open && !!guarantorSearchCriteria },
	);

	// if api error
	const error = isClaimsError || isGuarantorsError;
	// filter guarantors with unallocated amounts
	const guarantorWithUnallocatedAmounts = useMemo(() => {
		const filteredData = _.filter(
			guarantorsData?.data,
			(guarantor) => _.isNumber(guarantor?.metadata?.unallocated) && guarantor?.metadata?.unallocated > 0,
		);
		// if no guarantors found, return empty array
		if (guarantorsData?.count === 0) {
			return [];
		}
		// if no guarantors with unallocated amounts, set show confirm write off modal to true
		if (guarantorsData?.count > 0 && _.isEmpty(filteredData)) {
			// eslint-disable-next-line react-hooks/set-state-in-render
			setShowConfirmWriteOff(true);
		}
		return filteredData;
	}, [guarantorsData?.data]);

	// set form values
	useEffect(() => {
		form.setFieldsValue({
			amount: writeOffData?.amount ?? totalPR,
			reason: writeOffData?.reason ?? null,
		});
	}, [totalPR, writeOffData]);

	// handle close modal
	const handleClose = () => {
		setOpen(false);
		setShowConfirmWriteOff(false);
	};

	// handle save write off
	const handleSave = () => {
		form.validateFields().then((values) => {
			onWriteOffChange({
				claims: eligibleClaims,
				reason: values?.reason,
			});
			handleClose();
		});
	};

	// handle reset form
	const handleReset = () => {
		form.setFieldsValue({
			amount: writeOffData?.amount ?? totalPR,
			reason: null,
		});
	};

	// columns for guarantor table
	const columns: ColumnsType<Guarantor.IGuarantorResponse> = [
		{
			title: 'Guarantor',
			dataIndex: 'guarantorName',
			key: 'guarantorName',
			width: '40%',
			render: (_value, row) => getNameFromFhirResource(row?.fhirResource?.name),
		},
		{
			title: 'Unallocated',
			dataIndex: 'unallocatedAmount',
			key: 'unallocatedAmount',
			align: 'right',
			render: (_value, row) => formatAmount(row?.metadata?.unallocated),
		},
		{
			title: 'Action',
			dataIndex: 'unallocatedAmount',
			key: 'action',
			render: (_value, row) => (
				<Link
					to={getPaymentsPath({
						tab: ALLOCATION_MODES.unallocated.value,
						guarantorId: row?.fhirResource?.id,
					})}
				>
					<Button type='link' className='p-0' onClick={handleClose}>
						{'Allocate Payment'}
					</Button>
				</Link>
			),
		},
	];

	// get modal content
	const getModalContent = () => {
		// if api fails
		if (error) {
			return (
				<Flex vertical gap={24}>
					<Typography.Text strong className='text-base text-red-500'>
						{'Something went wrong while fetching the data.'}
					</Typography.Text>
				</Flex>
			);
		}
		// if write off modal to show, higher priority in if loop
		if (showConfirmWriteOff) {
			return (
				<Flex vertical gap={24}>
					{acknowledgeUnallocated && (
						<Flex vertical>
							<Typography.Text strong style={{ fontSize: 16 }}>
								{`${eligibleClaims?.length} claims are eligible for write-off.`}
							</Typography.Text>
							<Typography.Text italic type='secondary'>
								{"(Only claims in 'Payment Posted' state with a pending PR can be written off)"}
							</Typography.Text>
						</Flex>
					)}
					<Form layout='vertical' form={form}>
						<Form.Item label='Write-Off' name='amount' required rules={[{ required: true, message: 'Please enter an amount' }]}>
							<InputNumber readOnly disabled prefix='$' placeholder='Enter amount' className='w-full' />
						</Form.Item>

						<Form.Item label='Reason' name='reason' required rules={[{ required: true, message: 'Please enter a reason' }]}>
							<Select placeholder='Enter reason' options={writeOffReasonsOptions} loading={isLoading} allowClear />
						</Form.Item>
					</Form>
				</Flex>
			);
		}

		// if no eligible claims
		else if (_.isEmpty(eligibleClaims)) {
			// No eligible claims
			return (
				<Flex vertical gap={2}>
					<Typography.Text strong style={{ fontSize: 16 }}>
						{'None of the selected claims is eligible for write-off.'}
					</Typography.Text>
					<Typography.Text italic type='secondary'>
						{"(Only claims in 'Payment Posted' state with a pending PR can be written off)"}
					</Typography.Text>
				</Flex>
			);
		}

		// if guarantors with unallocated amounts
		else if (!_.isEmpty(guarantorWithUnallocatedAmounts)) {
			return (
				<Flex vertical gap={24}>
					{'The following guarantor(s) have unallocated amounts that may cover some of the pending patient responsibility.'}
					<Table
						bordered
						columns={columns}
						dataSource={guarantorWithUnallocatedAmounts}
						pagination={false}
						rowKey='id'
						size='small'
					/>
				</Flex>
			);
		}
		return null;
	};

	// get modal footer
	const getModalFooter = () => {
		// if write off modal to show
		if (showConfirmWriteOff) {
			return (
				<Flex justify='space-between'>
					<Button onClick={handleClose}>{'Cancel'}</Button>
					<Flex gap={8}>
						<Button type='link' onClick={handleReset}>
							{'Clear'}
						</Button>
						<Button type='primary' onClick={handleSave}>
							{'Submit'}
						</Button>
					</Flex>
				</Flex>
			);
		}

		// if no eligible claims or api error, just close button
		if (_.isEmpty(eligibleClaims) || error) {
			// No eligible claims - just close button
			return (
				<Flex justify='end'>
					<Button type='primary' onClick={handleClose}>
						{'Close'}
					</Button>
				</Flex>
			);
		}
		// if guarantors with unallocated amounts
		else if (!_.isEmpty(guarantorWithUnallocatedAmounts)) {
			return (
				<Flex justify='end' gap={8}>
					<Button onClick={handleClose}>{'Close'}</Button>
					<Button type='primary' onClick={() => setShowConfirmWriteOff(true)}>
						{'Proceed with Write-Off'}
					</Button>
				</Flex>
			);
		}
		return null;
	};

	return (
		<>
			<Modal
				title={'Confirm Write-Off'}
				destroyOnHidden
				open={open}
				onCancel={handleClose}
				footer={getModalFooter()}
				width={600}
				loading={isLoadingClaims || isLoadingGuarantors}
				className='identifier-modal'
				okButtonProps={{ loading: isPending }}
				cancelButtonProps={{ disabled: isPending }}
			>
				{getModalContent()}
			</Modal>

			<Button type='link' onClick={() => setOpen(true)} {...rest}>
				{showAddIcon && <Plus />} {'Write-Off'}
			</Button>
		</>
	);
};

export default WriteOff;
